import { useEffect, useState } from 'react';
import { Flame, Play, Pause, Headphones, Star } from 'lucide-react';
import { api } from '@/lib/api';
import { LoadingState, EmptyState } from '@/components/states';
import { usePlayer } from '@/context/PlayerContext';
import { ShareButton } from '@/components/actions';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=400';

export default function Hits() {
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const { playTrack, current, playing, toggle } = usePlayer();

  useEffect(() => {
    api.get('/hits').then(({ data }) => setHits(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const queue = hits.map((h) => ({
    song_id: h.song_id, title: h.title, slug: h.slug, cover_url: h.cover_url,
    audio_url: h.audio_url, style_text: h.style_text, duration_seconds: h.duration_seconds,
    creator: h.creator?.display_name,
  }));

  return (
    <div className="space-y-8">
      <div className="card-surface relative overflow-hidden p-8">
        <img src="https://images.pexels.com/photos/13872119/pexels-photo-13872119.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand/20 px-3 py-1 font-mono text-xs uppercase tracking-widest text-brand">
            <Flame className="h-3.5 w-3.5" /> Découverte
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            Meilleurs <span className="text-gradient-orange">Hits</span>
          </h1>
          <p className="mt-2 max-w-xl text-muted-foreground">Les meilleures créations publiques de la communauté.</p>
        </div>
      </div>

      {loading ? <LoadingState /> : hits.length === 0 ? (
        <EmptyState icon={Flame} title="Aucun hit pour le moment" description="Les morceaux publiés apparaîtront ici." />
      ) : (
        <div className="space-y-2">
          {hits.map((h, i) => {
            const isCurrent = current?.song_id === h.song_id;
            return (
              <div key={h.song_id} className="card-surface card-hover flex items-center gap-4 p-3" data-testid={`hit-${h.song_id}`}>
                <span className="w-6 text-center font-display text-lg font-bold text-muted-foreground">{i + 1}</span>
                <div className="relative h-14 w-14 overflow-hidden rounded-lg">
                  <img src={h.cover_url || FALLBACK} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => (isCurrent ? toggle() : (h.audio_url && playTrack({
                            song_id: h.song_id, title: h.title, slug: h.slug, cover_url: h.cover_url,
                            audio_url: h.audio_url, style_text: h.style_text, creator: h.creator?.display_name }, queue)))}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100" data-testid={`hit-play-${h.song_id}`}>
                    {isCurrent && playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 fill-white text-white" />}
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-semibold text-white">{h.title}</p>
                  <p className="truncate text-sm text-muted-foreground">{h.creator?.display_name || 'Artiste'} · {h.style_text}</p>
                </div>
                <div className="hidden items-center gap-4 font-mono text-xs text-muted-foreground sm:flex">
                  <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 text-brand" />{h.likes}</span>
                  <span className="flex items-center gap-1"><Headphones className="h-3.5 w-3.5" />{h.plays}</span>
                </div>
                <ShareButton slug={h.slug} title={h.title} creator={h.creator?.display_name} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
